import { FaroTooltip } from "@faro-lotv/flat-ui";
import { Box, ButtonBase } from "@mui/material";
import { Stack } from "@mui/system";
import { DateTime } from "luxon";
import { useMemo, type JSX } from "react";
import { MONTH_AND_YEAR, formatDate } from "../capture-date-format";
import { groupBucketsByMonth } from "../capture-date-stats";
import { CalendarHeader } from "./calendar-header";
import { bandSx, cellSx, rangeRoleOf } from "./calendar-styles";
import { captureLabel, useBucketCounts } from "./calendar-utils";
import {
  CalendarViewProps,
  CaptureGranularity,
} from "./capture-calendar-types";
import { CaptureCountChip } from "./capture-markers";

const MONTHS_PER_YEAR = 12;
const MONTHS_PER_ROW = 3;

/** @returns A year of oval month cells, each showing how many captures it holds */
export function MonthGrid({
  stats,
  startDate,
  endDate,
  onPick,
  onHover,
  isPickable,
  month,
  onMonthChange,
  bounds,
  onToggleView,
}: CalendarViewProps): JSX.Element {
  const months = useMemo(() => groupBucketsByMonth(stats.days), [stats.days]);
  const counts = useBucketCounts(months);
  const year = month.startOf("year");
  const thisMonth = DateTime.now().startOf("month");

  return (
    <Stack sx={{ userSelect: "none" }}>
      <CalendarHeader
        label={formatDate(year, { year: "numeric" })}
        granularity={CaptureGranularity.month}
        onToggleView={onToggleView}
        onPrevious={() => onMonthChange(month.minus({ years: 1 }))}
        onNext={() => onMonthChange(month.plus({ years: 1 }))}
        isPreviousDisabled={year <= bounds.min.startOf("year")}
        isNextDisabled={year >= bounds.max.startOf("year")}
      />

      <Box
        component="div"
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${MONTHS_PER_ROW}, 1fr)`,
          rowGap: 1,
        }}
      >
        {Array.from({ length: MONTHS_PER_YEAR }, (_, index) => {
          const cell = year.plus({ months: index });
          const count = counts.get(cell.toMillis()) ?? 0;
          const role = rangeRoleOf(cell, "month", startDate, endDate);
          const column = index % MONTHS_PER_ROW;
          const isDisabled =
            cell < bounds.min.startOf("month") ||
            cell > bounds.max ||
            !isPickable(cell, "month");

          return (
            <FaroTooltip
              key={cell.toMillis()}
              placement="top"
              title={`${formatDate(cell, MONTH_AND_YEAR)} — ${captureLabel(count)}`}
              sx={[
                bandSx(role, column === 0, column === MONTHS_PER_ROW - 1),
                { px: 0.75 },
              ]}
            >
              <ButtonBase
                aria-label={`${formatDate(cell, MONTH_AND_YEAR)} ${captureLabel(count)}`}
                aria-pressed={role.isSelected}
                disabled={isDisabled}
                onClick={(event) => onPick(cell, "month", event.shiftKey)}
                onPointerEnter={() => onHover?.([cell, "month"])}
                onPointerLeave={() => onHover?.()}
                sx={[
                  cellSx(role, cell.hasSame(thisMonth, "month"), isDisabled),
                  {
                    width: "100%",
                    height: "34px",
                    gap: 0.75,
                    fontSize: "13px",
                  },
                ]}
              >
                {formatDate(cell, { month: "short" })}
                {count > 0 && <CaptureCountChip count={count} />}
              </ButtonBase>
            </FaroTooltip>
          );
        })}
      </Box>
    </Stack>
  );
}
